import localhost from "apis/localhost";
import { JSON_CONTENT_TYPE } from "constants/app";

export const BASE_API_PATH = "/api/v1/technical-alignment/prompt-testing";

const HEADERS = {
  headers: {
    "Content-Type": JSON_CONTENT_TYPE,
  },
};

/**
 * Fetch current production prompts for extraction and synonym generation
 */
export const fetchCurrentPrompts = async () => {
  try {
    const url = `${BASE_API_PATH}/current-prompts`;
    const response = await localhost.get(url);
    return response?.data?.data || null;
  } catch (error) {
    console.error("Error fetching current prompts:", error);
    throw error;
  }
};

/**
 * Test custom prompts against opportunity documents
 *
 * @param {Object} params - Test parameters
 * @param {number} params.opportunityId - The opportunity ID to test against
 * @param {string} params.extractionPrompt - Custom extraction prompt
 * @param {string} params.synonymPrompt - Custom synonym generation prompt
 * @param {number} params.paragraphBatchSize - Batch size for paragraph processing (1-550)
 * @param {number} params.synonymBatchSize - Batch size for synonym generation (default: 150)
 * @param {number} params.synonymCount - Number of synonyms per term (default: 3)
 * @returns {Promise<Object>} Test results with extracted terms and metrics
 */
export const testPrompts = async ({
  opportunityId,
  extractionPrompt,
  synonymPrompt,
  paragraphBatchSize = 550,
  synonymBatchSize = 150,
  synonymCount = 3,
} = {}) => {
  try {
    const url = `${BASE_API_PATH}/test`;
    const params = {
      opportunityId,
      extractionPrompt,
      synonymPrompt,
      paragraphBatchSize,
      synonymBatchSize,
      synonymCount,
    };

    const response = await localhost.post(url, params, HEADERS);
    return response?.data?.data || null;
  } catch (error) {
    console.error("Error testing prompts:", error);
    throw error;
  }
};

/**
 * Export prompt test results to CSV
 *
 * @param {Object} results - The test results to export
 * @returns {Promise<Blob>} CSV file blob
 */
export const exportResultsToCsv = async (results) => {
  try {
    const url = `${BASE_API_PATH}/export-csv`;

    const response = await localhost.post(url, results, {
      ...HEADERS,
      responseType: "blob", // Important for file download
    });

    return response.data;
  } catch (error) {
    console.error("Error exporting results to CSV:", error);
    throw error;
  }
};

/**
 * Get list of requirement documents for an opportunity
 *
 * @param {number} opportunityId - The opportunity ID
 * @returns {Promise<Object>} Document information
 */
export const getOpportunityDocuments = async (opportunityId) => {
  try {
    const url = `${BASE_API_PATH}/opportunity/${opportunityId}/documents`;
    const response = await localhost.get(url);
    return response?.data?.data || null;
  } catch (error) {
    console.error("Error fetching opportunity documents:", error);
    throw error;
  }
};

/**
 * Test custom prompts with SSE streaming for real-time progress updates
 *
 * @param {Object} params - Test parameters including connectionId
 * @returns {Promise<Object>} Acknowledgment response
 */
export const testPromptsWithStreaming = async ({
  connectionId,
  opportunityId,
  extractionPrompt,
  synonymPrompt,
  paragraphBatchSize = 550,
  synonymBatchSize = 150,
  synonymCount = 3,
} = {}) => {
  try {
    const url = `${BASE_API_PATH}/test-stream`;
    const params = {
      connectionId,
      opportunityId,
      extractionPrompt,
      synonymPrompt,
      paragraphBatchSize,
      synonymBatchSize,
      synonymCount,
    };

    const response = await localhost.post(url, params, HEADERS);
    return response?.data?.data || null;
  } catch (error) {
    console.error("Error starting streaming prompt test:", error);
    throw error;
  }
};
